import { useEffect, useRef, useState } from 'react';
import { User, Bot, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import { cleanCompletionLink } from '@/utils/cleanCompletionLink';
import ImageLightbox from '@/components/ui/ImageLightbox';

interface MessageAttachment {
  file_id?: string;
  url?: string;
  type?: string;
  label?: string;
  transcription_text?: string;
}

interface TranscriptMessageProps {
  id: string;
  role: string;
  text: string;
  data?: MessageAttachment[];
  createdAt?: string;
  isHighlighted?: boolean;
}

function TranscriptMessage({
  id,
  role,
  text,
  data,
  createdAt,
  isHighlighted,
}: TranscriptMessageProps) {
  const { lang } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (!isHighlighted || !ref.current) return undefined;

    // Scroll after a delay to allow the Sheet open animation to complete
    // and media elements (video/audio) to load and settle their layout.
    // Re-scroll a second time after a longer delay to correct for any
    // layout shifts caused by lazy-loaded media resizing.
    const scrollToSelf = () => {
      // TELV1-418 — use an INSTANT scroll, not 'smooth'. Inside the Radix Sheet's
      // animating scroll container a programmatic smooth scrollIntoView silently
      // failed to land (verified: the cited message was correctly highlighted but
      // left ~760px below the fold). An instant scroll positions it reliably, and
      // the two-stage timing below still re-centres after lazy media resize.
      ref.current?.scrollIntoView({ behavior: 'auto', block: 'center' });
    };

    const t1 = setTimeout(scrollToSelf, 350);
    const t2 = setTimeout(scrollToSelf, 1200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isHighlighted]);

  const isAssistant = role === 'assistant';
  const { cleanText, completionUrl } = cleanCompletionLink(text);
  const audioItems = data?.filter((d) => d.type === 'audio' && d.url) || [];
  const videoItems = data?.filter((d) => d.type === 'video' && d.url) || [];
  const imageItems = data?.filter((d) => d.type === 'image' && d.url) || [];

  return (
    <div
      ref={ref}
      id={`msg-${id}`}
      className={cn(
        'flex gap-2.5 py-2 px-3 rounded-lg transition-colors duration-500',
        !isHighlighted && !isAssistant && 'bg-primary/15 border border-primary/30',
        isHighlighted && 'bg-primary/20 ring-1 ring-primary/40',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center h-6 w-6 rounded-full shrink-0 mt-0.5',
          isAssistant ? 'bg-neutral-100' : 'bg-primary/20',
        )}
      >
        {isAssistant ? (
          <Bot className="h-3.5 w-3.5 text-neutral-500" />
        ) : (
          <User className="h-3.5 w-3.5 text-primary" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-0.5">
          <span
            className={cn(
              'text-[11px] font-semibold uppercase tracking-wide',
              isAssistant ? 'text-neutral-400' : 'text-primary',
            )}
          >
            {isAssistant ? lang.get('msg.interviewer') : lang.get('msg.respondent')}
          </span>
          {createdAt && (
            <span className="text-[10px] text-neutral-300">
              {new Date(createdAt).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>

        {cleanText && (
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap break-words">
            {cleanText}
          </p>
        )}

        {completionUrl && (
          <div className="mt-2 flex flex-col gap-1 rounded-md bg-primary/5 px-3 py-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Complete Interview
            </span>
            <a
              href={completionUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-primary hover:underline break-all"
            >
              {completionUrl}
            </a>
          </div>
        )}

        {audioItems.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            {audioItems.map((item, idx) => (
              <div key={item.file_id || idx}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio src={item.url} controls preload="metadata" className="w-full h-8" />
                {item.transcription_text && (
                  <p className="text-xs text-neutral-400 mt-1 italic">
                    &ldquo;{item.transcription_text}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {videoItems.length > 0 && (
          <div className="flex flex-col gap-2 mt-2">
            {videoItems.map((item, idx) => (
              <div key={item.file_id || idx}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  src={item.url}
                  controls
                  preload="metadata"
                  className="w-full rounded-md border border-neutral-200"
                />
                {item.transcription_text && (
                  <p className="text-xs text-neutral-400 mt-1 italic">
                    &ldquo;{item.transcription_text}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {imageItems.length > 0 && (
          <div className={cn('mt-2 gap-2', imageItems.length > 1 ? 'grid grid-cols-2' : 'flex')}>
            {imageItems.map((item, idx) => (
              <button
                key={item.file_id || idx}
                type="button"
                onClick={() => {
                  setLightboxIndex(idx);
                  setLightboxOpen(true);
                }}
                className="block w-full p-0 border-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-md"
                aria-label={item.label || lang.get('msg.respondentImage') || 'Open image'}
              >
                <img
                  src={item.url}
                  alt={item.label || 'Respondent image'}
                  className="w-full rounded-md object-cover border border-neutral-200 hover:opacity-80 transition-opacity cursor-zoom-in"
                />
              </button>
            ))}
          </div>
        )}
      </div>
      {imageItems.length > 0 && (
        <ImageLightbox
          images={imageItems
            .filter((item): item is typeof item & { url: string } => !!item.url)
            .map((item) => ({
              url: item.url,
              caption: item.label,
            }))}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      )}
    </div>
  );
}

export default TranscriptMessage;
